/*
 * Video slider block behaviour.
 *
 * The carousel itself is powered by the theme's existing Splide build
 * (loaded through the Alpine `xSplide` store, see the block markup).
 * This file only handles video playback, the custom controls, drag-click
 * suppression and section-editor re-initialisation.
 */
(function () {
  'use strict';

  if (window.__xVideoSliderModule) return;
  window.__xVideoSliderModule = true;

  var reduceMotion =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function formatTime(seconds) {
    if (!isFinite(seconds) || seconds < 0) seconds = 0;
    var total = Math.floor(seconds);
    var minutes = Math.floor(total / 60);
    var secs = total % 60;
    return minutes + ':' + (secs < 10 ? '0' + secs : secs);
  }

  // Shopify exposes the upload duration in milliseconds.
  function normalizeDuration(value) {
    var n = parseFloat(value);
    if (!isFinite(n) || n <= 0) return 0;
    if (n > 1000) n = n / 1000;
    return n;
  }

  function each(selector, scope, callback) {
    var nodes = scope.querySelectorAll(selector);
    for (var i = 0; i < nodes.length; i++) callback(nodes[i]);
  }

  function setText(scope, selector, value) {
    each(selector, scope, function (node) {
      node.textContent = value;
    });
  }

  function setDuration(item, seconds) {
    setText(item.slide, '[data-video-duration]', formatTime(seconds));
    var progress = item.slide.querySelector('.video-slider__progress');
    if (progress) progress.setAttribute('aria-valuemax', String(Math.round(seconds)));
  }

  function updateTime(item) {
    setText(item.slide, '[data-video-current]', formatTime(item.video.currentTime));
  }

  function updateProgress(item, ratio) {
    if (!isFinite(ratio) || ratio < 0) ratio = 0;
    if (ratio > 1) ratio = 1;
    var fill = item.slide.querySelector('.video-slider__progress-fill');
    if (fill) fill.style.width = ratio * 100 + '%';
    var progress = item.slide.querySelector('.video-slider__progress');
    if (progress) progress.setAttribute('aria-valuenow', String(Math.round(ratio * 100)));
  }

  function updatePlayControl(item) {
    var button = item.slide.querySelector('.video-slider__playpause');
    if (!button) return;
    var playing = !item.video.paused && !item.video.ended;
    button.setAttribute('aria-pressed', playing ? 'true' : 'false');
    button.setAttribute('aria-label', playing ? 'Pause video' : 'Play video');
    var playIcon = button.querySelector('.video-slider__icon-play');
    var pauseIcon = button.querySelector('.video-slider__icon-pause');
    if (playIcon) playIcon.hidden = playing;
    if (pauseIcon) pauseIcon.hidden = !playing;
  }

  function updateMuteControl(item) {
    var button = item.slide.querySelector('.video-slider__mute');
    if (!button) return;
    var muted = item.video.muted;
    button.setAttribute('aria-pressed', muted ? 'true' : 'false');
    button.setAttribute('aria-label', muted ? 'Unmute video' : 'Mute video');
    var mutedIcon = button.querySelector('.video-slider__icon-muted');
    var soundIcon = button.querySelector('.video-slider__icon-sound');
    if (mutedIcon) mutedIcon.hidden = !muted;
    if (soundIcon) soundIcon.hidden = muted;
  }

  function pauseOthers(root, current) {
    each('video', root, function (video) {
      if (video !== current && !video.paused) video.pause();
    });
  }

  function togglePlay(video) {
    if (video.paused || video.ended) {
      // Sound is on by default: the video element is not muted in the markup.
      var promise = video.play();
      if (promise && promise.catch) {
        promise.catch(function () {
          video.muted = true;
          var retry = video.play();
          if (retry && retry.catch) retry.catch(function () {});
        });
      }
    } else {
      video.pause();
    }
  }

  function resetPoster(video) {
    try {
      video.currentTime = 0;
      video.load();
    } catch (error) {
      /* noop */
    }
  }

  function bindSeek(item) {
    var progress = item.slide.querySelector('.video-slider__progress');
    if (!progress) return;
    var dragging = false;

    function ratioFromEvent(event) {
      var rect = progress.getBoundingClientRect();
      if (!rect.width) return 0;
      var clientX =
        event.touches && event.touches.length ? event.touches[0].clientX : event.clientX;
      return Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    }

    function seek(event) {
      var ratio = ratioFromEvent(event);
      if (item.video.duration > 0) item.video.currentTime = ratio * item.video.duration;
      updateProgress(item, ratio);
      updateTime(item);
    }

    function stop() {
      dragging = false;
    }

    progress.addEventListener('pointerdown', function (event) {
      event.stopPropagation();
      dragging = true;
      if (progress.setPointerCapture) {
        try {
          progress.setPointerCapture(event.pointerId);
        } catch (error) {
          /* noop */
        }
      }
      seek(event);
    });
    progress.addEventListener('pointermove', function (event) {
      if (!dragging) return;
      event.stopPropagation();
      seek(event);
    });
    progress.addEventListener('pointerup', stop);
    progress.addEventListener('pointercancel', stop);
    progress.addEventListener('click', function (event) {
      event.stopPropagation();
    });
    progress.addEventListener('keydown', function (event) {
      if (!item.video.duration) return;
      var step = item.video.duration / 20;
      if (event.key === 'ArrowRight' || event.key === 'ArrowUp') {
        item.video.currentTime = Math.min(
          item.video.duration,
          item.video.currentTime + step
        );
        event.preventDefault();
      } else if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') {
        item.video.currentTime = Math.max(0, item.video.currentTime - step);
        event.preventDefault();
      }
    });
  }

  function applyReducedMotion(root) {
    if (!reduceMotion) return;
    var splideElement = root.querySelector('.x-video-slider');
    if (
      splideElement &&
      splideElement.splide &&
      splideElement.splide.options.speed !== 0
    ) {
      splideElement.splide.options = { speed: 0 };
    }
  }

  function setupBlock(root) {
    if (root.__vsReady) return;
    root.__vsReady = true;

    var start = { x: 0, y: 0, active: false };

    // Suppress the click that follows a drag so swiping never toggles playback.
    // Splide already does this on the track; this is a safeguard.
    root.addEventListener(
      'pointerdown',
      function (event) {
        root.__vsDragged = false;
        if (event.target.closest('.video-slider__no-drag, .video-slider__controls')) return;
        start.x = event.clientX;
        start.y = event.clientY;
        start.active = true;
      },
      true
    );
    root.addEventListener(
      'pointermove',
      function (event) {
        if (!start.active) return;
        if (Math.abs(event.clientX - start.x) > 5 || Math.abs(event.clientY - start.y) > 5) {
          root.__vsDragged = true;
        }
      },
      true
    );
    root.addEventListener('pointerup', function () {
      start.active = false;
    }, true);
    root.addEventListener('pointercancel', function () {
      start.active = false;
    }, true);

    each('.video-slider__slide', root, function (slide) {
      var video = slide.querySelector('video');
      if (!video) return;
      var item = { root: root, slide: slide, video: video };

      var initial = normalizeDuration(video.getAttribute('data-duration'));
      if (initial) setDuration(item, initial);

      video.addEventListener('loadedmetadata', function () {
        if (isFinite(video.duration) && video.duration > 0) setDuration(item, video.duration);
      });
      video.addEventListener('play', function () {
        pauseOthers(root, video);
        slide.classList.add('is-playing');
        updatePlayControl(item);
      });
      video.addEventListener('pause', function () {
        slide.classList.remove('is-playing');
        updatePlayControl(item);
      });
      video.addEventListener('ended', function () {
        slide.classList.remove('is-playing');
        updatePlayControl(item);
        updateTime(item);
        updateProgress(item, 0);
        resetPoster(video);
      });
      video.addEventListener('volumechange', function () {
        updateMuteControl(item);
      });
      video.addEventListener('timeupdate', function () {
        updateTime(item);
        if (video.duration > 0) updateProgress(item, video.currentTime / video.duration);
      });

      slide.addEventListener('click', function (event) {
        if (event.target.closest('.video-slider__controls')) return;
        if (event.target.closest('.splide__arrow')) return;
        if (root.__vsDragged) {
          root.__vsDragged = false;
          return;
        }
        togglePlay(video);
      });

      var playButton = slide.querySelector('.video-slider__playpause');
      if (playButton) {
        playButton.addEventListener('click', function (event) {
          event.preventDefault();
          event.stopPropagation();
          togglePlay(video);
        });
      }

      var muteButton = slide.querySelector('.video-slider__mute');
      if (muteButton) {
        muteButton.addEventListener('click', function (event) {
          event.preventDefault();
          event.stopPropagation();
          video.muted = !video.muted;
          updateMuteControl(item);
        });
      }

      bindSeek(item);
      updatePlayControl(item);
      updateMuteControl(item);
      updateTime(item);
      updateProgress(item, 0);
    });

    applyReducedMotion(root);
  }

  function refreshWithin(scope) {
    if (!scope || !scope.querySelectorAll) return;
    var nodes = [];
    if (scope.matches && scope.matches('video-slider')) nodes.push(scope);
    nodes = nodes.concat(Array.prototype.slice.call(scope.querySelectorAll('video-slider')));
    for (var i = 0; i < nodes.length; i++) {
      if (nodes[i].refresh) nodes[i].refresh();
    }
  }

  var VideoSliderElement = (function () {
    if (window.customElements && window.HTMLElement) {
      return class VideoSliderElement extends HTMLElement {
        connectedCallback() {
          if (!this.__vsBound) {
            this.__vsBound = true;
            this.__vsOnUpdate = this.refresh.bind(this);
            document.addEventListener('video-slider:updated', this.__vsOnUpdate);
          }
          this.refresh();
        }
        disconnectedCallback() {
          if (this.__vsBound) {
            document.removeEventListener('video-slider:updated', this.__vsOnUpdate);
            this.__vsBound = false;
          }
        }
        refresh() {
          setupBlock(this);
          applyReducedMotion(this);
        }
      };
    }
    return null;
  })();

  if (VideoSliderElement && !customElements.get('video-slider')) {
    customElements.define('video-slider', VideoSliderElement);
  } else if (!VideoSliderElement) {
    // Very old browsers without custom element support.
    document.addEventListener('DOMContentLoaded', function () {
      var nodes = document.querySelectorAll('video-slider');
      for (var i = 0; i < nodes.length; i++) setupBlock(nodes[i]);
    });
  }

  document.addEventListener('shopify:section:load', function (event) {
    refreshWithin(event.target);
  });
  document.addEventListener('shopify:block:select', function (event) {
    refreshWithin(event.target);
  });
})();
